import React, { useEffect, useState } from "react";
import Footer from "../../core/common/footer/Footer";
import { useNavigate } from "react-router";
import { all_routes } from "../router/all_routes";
import CandidateDetails from "./components/CandidateDetails";
import YourLocation from "./components/YourLocation";
import CancelAuthentication from "./components/CancelAuthentication";
import StepProgress from "./components/StepProgress";
import { z } from "zod";
import { useSelector } from "react-redux";
import useApi from "../../core/customhook/useApi";
import { getPersonalDetails } from "../../environment";
import Swal from "sweetalert2";
import SnipperLoader from "../../core/common/loader/SnipperLoader";

// Zod schema for the verification form
const verificationFormSchema = z.object({
  verifier_full_name: z.string().trim().min(1, "Verifier name is required"),
  verifier_relationship: z
    .string()
    .trim()
    .min(1, "Relation with verifier is required"),
  verifier_addrlandmark: z
    .string()
    .trim()
    .min(1, "Nearest landmark is required"),
  period_of_stay: z
    .string()
    .trim()
    .min(1, "Period of stay is required")
    .refine((val) => /^\d+$/.test(val) && Number(val) > 0, {
      message: "Enter a valid period (positive number)",
    }),
  period_of_stay_param: z
    .string()
    .trim()
    .refine((val) => ["Months", "Years"].includes(val), {
      message: "Please select a valid period type",
    }),
  ownership_status: z
    .string()
    .trim()
    .min(1, "Please select an ownership status")
    .refine((val) => ["Owned", "Rented Home", "Rented PG"].includes(val), {
      message: "Please select a valid ownership status",
    }),
});

type VerificationFormData = z.infer<typeof verificationFormSchema>;
type FormErrors = Partial<Record<keyof VerificationFormData, string>>;

const TemplateQuestion = () => {
  const navigate = useNavigate();

  const reduxDashboardData = useSelector(
    (state: any) => state?.dashboard?.dashboardData,
  );

  const candidateId = reduxDashboardData?.assignmentDetails?.candidate_id;

  const getAddressAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const updateSelfieAPI = useApi<any>(
    `/api/v1/candidates/addresses/${reduxDashboardData?.assignmentDetails?.candidate_id}/stay-details`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [personalFormData, setPersonalFormData] = useState<any>({});
  const [detailData, setDetailData] = useState<any>(null);

  const PersonalDetailAPI = useApi<any>(getPersonalDetails, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const [originalFormData, setOriginalFormData] =
    useState<VerificationFormData | null>(null);
  const [formData, setFormData] = useState<VerificationFormData>({
    verifier_full_name: "",
    verifier_relationship: "",
    verifier_addrlandmark: "",
    period_of_stay: "",
    period_of_stay_param: "Months",
    ownership_status: "",
  });

  const getProfileData = async () => {
    try {
      const response = await PersonalDetailAPI.callApi();

      if (response?.success) {
        setPersonalFormData(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getProfileData();
  }, []);

  useEffect(() => {
    if (!candidateId) return;
    const fetchDetails = async () => {
      try {
        const res = await getAddressAPI.callApi();

        if (res?.success) {
          const data = res.data[0];

          setDetailData(data);

          const initialData: VerificationFormData = {
            verifier_full_name: data.verifier_full_name || "",
            verifier_relationship: data.verifier_relationship || "",
            verifier_addrlandmark: data.verifier_addrlandmark || "",
            period_of_stay: data.period_of_stay.toString() || "",
            period_of_stay_param: data.period_of_stay_param || "Months",
            ownership_status: data.ownership_status || "",
          };

          setFormData(initialData);
          setOriginalFormData(initialData);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchDetails();
  }, [candidateId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear the field's error as soon as the user edits it
    if (errors[name as keyof VerificationFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleNext = async () => {
    const result = verificationFormSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof VerificationFormData;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    const hasChanges =
      JSON.stringify(formData) !== JSON.stringify(originalFormData);

    if (originalFormData && !hasChanges) {
      navigate(all_routes.documentUpload);
      return;
    }
    try {
      const response = await updateSelfieAPI.callApi({
        ...formData,
        address_type: detailData?.address_type,
      });

      if (response?.success) {
        navigate(all_routes.documentUpload);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {getAddressAPI.loading ? (
          <SnipperLoader />
        ) : (
          <>
            <StepProgress
              totalSteps={5}
              currentStep={4}
              completedSteps={[1, 2, 3]}
            />

            <div className="card mb-4">
              <div className="card-header bg-white py-3">
                <h4 className="fw-bold mb-0">
                  <i
                    className="ti ti-user-check me-2"
                    style={{ color: "#F26522" }}
                  ></i>
                  Address & Live Selfie Verification
                </h4>
              </div>

              <div className="card-body">
                {/* ================= Personal Details ================= */}

                <div className="d-flex align-items-center mb-3">
                  <i
                    className="ti ti-user me-2"
                    style={{ color: "#F26522", fontSize: "20px" }}
                  ></i>

                  <h5 className="fw-semibold mb-0">Personal Details</h5>
                </div>

                {/* Responsive Table Wrapper */}
                <div className="table-responsive">
                  <table
                    className="table table-bordered align-middle mb-0"
                    style={{ minWidth: "750px" }}
                  >
                    <tbody>
                      <tr>
                        <th style={{ width: "16%" }}>First Name</th>
                        <td>{personalFormData?.first_name || "-"}</td>

                        <th style={{ width: "16%" }}>Middle Name</th>
                        <td>{personalFormData?.middle_name || "-"}</td>

                        <th style={{ width: "16%" }}>Last Name</th>
                        <td>{personalFormData?.last_name || "-"}</td>
                      </tr>

                      <tr>
                        <th>Date of Birth</th>
                        <td>
                          {personalFormData?.dob
                            ? new Date(personalFormData.dob)
                                .toISOString()
                                .split("T")[0]
                            : "-"}
                        </td>

                        <th>Father's Name</th>
                        <td colSpan={3}>
                          {personalFormData?.father_or_mother_name || "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <hr style={{ margin: "25px 0", borderColor: "#919297" }} />

                {/* ================= Contact Details ================= */}

                <div className="d-flex align-items-center mb-3">
                  <i
                    className="ti ti-phone me-2"
                    style={{ color: "#F26522", fontSize: "20px" }}
                  ></i>

                  <h5 className="fw-semibold mb-0">Contact Details</h5>
                </div>

                {/* Responsive Table Wrapper */}
                <div className="table-responsive">
                  <table
                    className="table table-bordered align-middle mb-0"
                    style={{ minWidth: "650px" }}
                  >
                    <tbody>
                      <tr>
                        <th style={{ width: "20%" }}>Mobile Number</th>
                        <td>{personalFormData?.phone || "-"}</td>

                        <th style={{ width: "20%" }}>
                          Alternate Mobile Number
                        </th>
                        <td>{personalFormData?.alternative_phone || "-"}</td>
                      </tr>

                      <tr>
                        <th>Email Address</th>
                        <td colSpan={3}>{personalFormData?.email || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <CandidateDetails addressInfo={detailData} />
            <YourLocation addressInfo={detailData} />
            <div className="card p-4 overflow-hidden">
              <div className="border-bottom pb-3 mb-4">
                <h5 className="fw-semibold mb-0">Verification form</h5>
              </div>

              <div className="card-body p-0">
                <div className="row">
                  {/* Verifier Name */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Verifier Name
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <input
                      type="text"
                      name="verifier_full_name"
                      className={`form-control ${
                        errors.verifier_full_name ? "is-invalid" : ""
                      }`}
                      placeholder="Enter Verifier Name"
                      value={formData.verifier_full_name}
                      onChange={handleChange}
                    />
                    {errors.verifier_full_name && (
                      <div className="invalid-feedback">
                        {errors.verifier_full_name}
                      </div>
                    )}
                  </div>

                  {/* Relation With Verifier */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Relation With Verifier
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <input
                      type="text"
                      name="verifier_relationship"
                      className={`form-control ${
                        errors.verifier_relationship ? "is-invalid" : ""
                      }`}
                      placeholder="Enter Relation With Verifier"
                      value={formData.verifier_relationship}
                      onChange={handleChange}
                    />
                    {errors.verifier_relationship && (
                      <div className="invalid-feedback">
                        {errors.verifier_relationship}
                      </div>
                    )}
                  </div>

                  {/* Nearest Landmark */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Nearest Landmark
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <input
                      type="text"
                      name="verifier_addrlandmark"
                      className={`form-control ${
                        errors.verifier_addrlandmark ? "is-invalid" : ""
                      }`}
                      placeholder="Enter Nearest Landmark"
                      value={formData.verifier_addrlandmark}
                      onChange={handleChange}
                    />
                    {errors.verifier_addrlandmark && (
                      <div className="invalid-feedback">
                        {errors.verifier_addrlandmark}
                      </div>
                    )}
                  </div>

                  {/* Nature of Address */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Address Type
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="address_type"
                      className="form-control"
                      value={detailData?.address_type}
                      disabled
                    />
                  </div>

                  {/* Period of Stay */}
                  <div className="col-md-6">
                    <div className="row d-flex align-items-center">
                      <div className="col-md-6">
                        <label className="form-label fw-medium">
                          Period Type
                        </label>
                        <select
                          name="period_of_stay_param"
                          className={`form-select ${
                            errors.period_of_stay_param ? "is-invalid" : ""
                          }`}
                          value={formData.period_of_stay_param}
                          onChange={handleChange}
                        >
                          {/* <option value="Days">Days</option> */}
                          <option value="Months">Months</option>
                          <option value="Years">Years</option>
                        </select>
                        {errors.period_of_stay_param && (
                          <div className="invalid-feedback">
                            {errors.period_of_stay_param}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-medium">
                          Period of Stay
                          <span className="text-danger ms-1">*</span>
                        </label>

                        <input
                          type="number"
                          name="period_of_stay"
                          className={`form-control ${
                            errors.period_of_stay ? "is-invalid" : ""
                          }`}
                          placeholder={
                            formData.period_of_stay_param === "Years"
                              ? "Enter number of years, e.g. 2"
                              : "Enter number of months, e.g. 6"
                          }
                          value={formData.period_of_stay}
                          onChange={handleChange}
                        />
                        {errors.period_of_stay && (
                          <div className="invalid-feedback">
                            {errors.period_of_stay}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Ownership Status */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Ownership Status
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <select
                      name="ownership_status"
                      className={`form-select ${
                        errors.ownership_status ? "is-invalid" : ""
                      }`}
                      value={formData.ownership_status}
                      onChange={handleChange}
                    >
                      <option value="">Select Status</option>
                      <option value="Owned">Owned</option>
                      <option value="Rented Home">Rented Home</option>
                      <option value="Rented PG">Rented PG</option>
                    </select>
                    {errors.ownership_status && (
                      <div className="invalid-feedback">
                        {errors.ownership_status}
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3 mt-4">
                  {/* <button
                className="btn btn-outline-primary"
                onClick={() => setShowCancelModal(true)}
              >
                Cancel
              </button> */}
                  <button
                    // onClick={() => setShowCancelModal(true)}
                    onClick={() => navigate(-1)}
                    className="btn btn-outline-primary"
                  >
                    Back
                  </button>
                  <button className="btn btn-primary" onClick={handleNext}>
                    Save & Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {showCancelModal && (
          <CancelAuthentication
            show={showCancelModal}
            onClose={() => setShowCancelModal(false)}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default TemplateQuestion;
